import { ActiveDescendantKeyManager } from '@angular/cdk/a11y'
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
  forwardRef,
} from '@angular/core'
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms'
import { Observable, Subject, of } from 'rxjs'
import { catchError, debounceTime, finalize, map, switchMap, takeUntil } from 'rxjs/operators'
import { CallerService } from '../services/caller.service'
import { PublicCorpMember } from '../ngrx-store/app.state'
import { displayNameOf } from '../utils/corp-member.util'
import { MentionOptionDirective } from './mention-option.directive'

export interface MentionRange {
  /** CorpMember slug_id, falling back to a stringified numeric id if a candidate somehow
   *  lacks one. */
  id: string
  name: string
  start: number
  end: number
}

export interface MentionDraft {
  text: string
  mentions: MentionRange[]
}

/**
 * Self-contained @mention-autocomplete textarea. Behaves as a standard `ControlValueAccessor`
 * (bind with `[formControl]`/`formControlName` like any other input) for the friendly "@Name"
 * text; callers that need the wire format (`@[[id:Name]]`) or need to restore a draft without
 * losing mention formatting (e.g. after a failed send) use buildWireMessage()/captureDraft()/
 * restoreDraft() directly via a template reference or @ViewChild.
 */
@Component({
  selector: 'app-mention-textarea',
  templateUrl: './mention-textarea.component.html',
  styleUrls: ['./mention-textarea.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MentionTextareaComponent),
      multi: true,
    },
  ],
})
export class MentionTextareaComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @Input() label = 'Enter a message'

  // Unique per instance so aria-controls/aria-activedescendant/option ids never collide if more
  // than one of this component is ever rendered on the same page at once.
  private static nextInstanceId = 0
  readonly instanceId = `mention-textarea-${MentionTextareaComponent.nextInstanceId++}`

  @ViewChild('textareaEl', { read: ElementRef, static: false })
  private textareaRef?: ElementRef<HTMLTextAreaElement>

  @ViewChildren(MentionOptionDirective) private options!: QueryList<MentionOptionDirective>
  // Arrow keys move through the suggestions (wrapping at either end); the textarea keeps focus and
  // points at the highlighted one with aria-activedescendant.
  private keyManager?: ActiveDescendantKeyManager<MentionOptionDirective>

  private destroy$ = new Subject<void>()

  internalControl = new FormControl<string>('', { nonNullable: true })

  // @mention autocomplete state.
  mentionResults: PublicCorpMember[] = []
  showMentionPanel = false
  // Bound as the panel's max-height, and used to decide whether it fits above the textarea.
  // 5.5 rows: the half row cut off at the bottom shows there are more to scroll to.
  readonly mentionPanelMaxHeight = 198
  // Whether the dropdown renders above or below the textarea — recomputed each time it opens
  // so it doesn't render off-screen/clipped when the textarea is near the top of the viewport.
  mentionPanelPosition: 'above' | 'below' = 'above'
  private mentionQuery$ = new Subject<string>()
  // Index in internalControl.value where the in-progress "@" token starts, or -1 when none is active.
  private mentionTokenStart = -1
  // Caches each query's result set, keyed by lowercased query text, so narrowing an existing
  // query (typing more characters) can filter in memory instead of hitting the network. Must
  // match the `limit: 20` in corpers.online/services/corpMemberService.js searchForMention —
  // `truncated` means the server may have more matches than we were given, so a narrower query
  // built from this entry can't be trusted as exhaustive and needs a real request.
  private static readonly MENTION_SEARCH_RESULT_LIMIT = 20
  private mentionSearchCache = new Map<string, { results: PublicCorpMember[]; truncated: boolean }>()
  // Queries with a network request currently in flight — prevents firing a duplicate request
  // for the same text (e.g. the focus-time prefetch and a just-typed "@" both wanting query '').
  private pendingQueries = new Set<string>()
  // The query currently being displayed/awaited, so a slow-resolving request for a stale query
  // (e.g. the focus-time prefetch resolving after the user has since typed real characters)
  // can't clobber whatever the dropdown is currently showing.
  private activeQuery: string | null = null
  private hasPrefetchedDefaults = false
  // Mentions currently inserted in the visible textarea text, tracked as plain "@Name" ranges
  // (never the raw @[[id:Name]] wire token — that's only ever built at send time in
  // buildWireMessage()) so the compose box always shows a human-friendly name.
  private activeMentions: MentionRange[] = []
  private lastValue = ''

  private onChange: (value: string) => void = () => {}
  private onTouched: () => void = () => {}

  constructor(private callerService: CallerService, private cdf: ChangeDetectorRef) {
    this.internalControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.onChange(value)
    })

    this.mentionQuery$
      .pipe(
        debounceTime(300),
        switchMap((query) => this.fetchMentionCandidates(query)),
        takeUntil(this.destroy$)
      )
      .subscribe(({ query, results, failed }) => this.handleSearchResult(query, results, failed))
  }

  ngAfterViewInit(): void {
    this.keyManager = new ActiveDescendantKeyManager(this.options).withWrap()
  }

  /**
   * Proactively warms the default (empty-query) suggestion cache as soon as the compose box is
   * focused — well before the user has typed "@" — instead of only starting that network round
   * trip once they've typed "@" and are already waiting to see something appear. On a slow
   * connection this is the difference between the default list showing instantly and a visible
   * blank-dropdown stall. Bypasses the 300ms debounce below (that exists to damp a burst of
   * keystrokes; a one-off focus event has nothing to debounce against) and goes straight to the
   * network, sharing the same cache/dedup/staleness machinery as a real typed query.
   */
  onTextareaFocus(): void {
    if (this.hasPrefetchedDefaults) return
    this.hasPrefetchedDefaults = true
    this.fetchMentionCandidates('')
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ query, results, failed }) => this.handleSearchResult(query, results, failed))
  }

  private fetchMentionCandidates(
    query: string
  ): Observable<{ query: string; results: PublicCorpMember[]; failed: boolean }> {
    this.pendingQueries.add(query)
    return this.callerService.searchCorpMembersForMention(query).pipe(
      // JSONHttpOptions uses observe: 'response', so the JSON payload is at res.body, not res.
      map((res: any) => ({ query, results: (res?.body?.data ?? []) as PublicCorpMember[], failed: false })),
      catchError(() => of({ query, results: [] as PublicCorpMember[], failed: true })),
      // Runs on success, error, AND cancellation (e.g. switchMap replacing this with a newer
      // query) — without this, a query cancelled mid-flight would stay "pending" forever and
      // triggerMentionSearch() would silently refuse to ever retry it.
      finalize(() => this.pendingQueries.delete(query))
    )
  }

  private handleSearchResult(query: string, results: PublicCorpMember[], failed: boolean): void {
    // Don't cache a transient failure as "this query has zero results" — a retry later (e.g.
    // after the connection recovers) should still hit the network, not a poisoned cache entry.
    if (!failed) {
      this.mentionSearchCache.set(query, {
        results,
        truncated: results.length >= MentionTextareaComponent.MENTION_SEARCH_RESULT_LIMIT,
      })
    }
    // Only reflect these results in the live dropdown if they're for the query currently being
    // shown/awaited — guards against a slow prefetch (or a stale keystroke) resolving after the
    // user has moved on and clobbering what's actually on screen.
    if (this.activeQuery === query) {
      this.applyMentionResults(results)
    }
  }

  private triggerMentionSearch(query: string): void {
    if (this.pendingQueries.has(query)) return
    this.mentionQuery$.next(query)
  }

  // ==== ControlValueAccessor ====

  writeValue(value: string | null): void {
    const next = value ?? ''
    // emitEvent: false — this write originated from the parent (e.g. clearing after a send), so
    // there's nothing new to notify it of.
    this.internalControl.setValue(next, { emitEvent: false })
    this.lastValue = next
    this.activeMentions = []
    this.closeMentionPanel()
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) this.internalControl.disable()
    else this.internalControl.enable()
  }

  // ==== Public API for the host component ====

  /** Reconstructs the @[[id:Name]] wire format from the friendly text shown in the textarea. */
  buildWireMessage(): string {
    const visibleText = this.internalControl.value ?? ''
    if (!this.activeMentions.length) return visibleText

    const sorted = [...this.activeMentions].sort((a, b) => a.start - b.start)
    let result = ''
    let cursor = 0
    for (const mention of sorted) {
      result += visibleText.slice(cursor, mention.start)
      result += `@[[${mention.id}:${mention.name}]]`
      cursor = mention.end
    }
    result += visibleText.slice(cursor)
    return result
  }

  /** Snapshot of the current friendly text + mention ranges, so a caller can restore it later
   *  (e.g. after a failed send) without losing @mention formatting. */
  captureDraft(): MentionDraft {
    return { text: this.internalControl.value ?? '', mentions: [...this.activeMentions] }
  }

  /** Restores a previously captured draft, preserving its @mention formatting — unlike
   *  `writeValue()` (used e.g. to clear the box after a successful send), which always treats
   *  incoming text as mention-free. */
  restoreDraft(draft: MentionDraft): void {
    this.activeMentions = [...draft.mentions]
    this.lastValue = draft.text
    this.internalControl.setValue(draft.text)
    this.closeMentionPanel()
  }

  focus(): void {
    this.textareaRef?.nativeElement?.focus()
  }

  onBlur(): void {
    this.closeMentionPanel()
    this.onTouched()
  }

  get listboxId(): string {
    return `${this.instanceId}-listbox`
  }

  /** id of the currently-highlighted option, for aria-activedescendant — null when the dropdown
   *  isn't showing any results, so the attribute is omitted entirely rather than pointing at
   *  nothing. */
  get activeOptionId(): string | null {
    return this.showMentionPanel && this.mentionResults.length ? this.keyManager?.activeItem?.id ?? null : null
  }

  optionId(index: number): string {
    return `${this.instanceId}-option-${index}`
  }

  /**
   * Detects an in-progress "@word" immediately before the caret and triggers a search. Also
   * bound to (click)/(keyup) on the textarea, not just (input) — clicking elsewhere in the same
   * textarea or navigating with arrow keys moves the caret without firing input/blur, so without
   * re-running this the dropdown state (and mentionTokenStart) would silently go stale.
   */
  onMessageInput(event: Event): void {
    // Read straight off the DOM element rather than `this.internalControl.value` — the native
    // `input` event fires both this handler and the FormControl's own value-accessor listener
    // on the same element, and their relative order isn't guaranteed, so `internalControl.value`
    // can still reflect the previous keystroke here.
    const textarea = event.target as HTMLTextAreaElement
    const value = textarea.value ?? ''
    this.reconcileMentionsAfterEdit(value)

    const caret = textarea.selectionStart ?? value.length
    const textBeforeCaret = value.slice(0, caret)
    const match = /(?:^|\s)@(\w*)$/.exec(textBeforeCaret)

    if (match) {
      this.mentionTokenStart = caret - match[1].length - 1
      this.showMentionPanel = true
      this.mentionPanelPosition = this.computeMentionPanelPosition(textarea)
      this.searchMentionCandidates(match[1])
    } else {
      this.closeMentionPanel()
    }
  }

  /**
   * (keyup) handler for the textarea — deliberately NOT the same handler as (keydown)/(input).
   * onMessageInput() needs to re-run on keyup too (arrow-key caret movement doesn't fire `input`),
   * but only for keys onMentionKeydown() didn't already fully own: ArrowUp/ArrowDown/Enter/Tab/
   * Escape are preventDefault()ed there and never move the caret, so re-running detection for them
   * here would find the exact same query, hit the result cache, and call applyMentionResults()
   * again — which highlights the first option again, silently undoing the arrow key's own update.
   */
  onMessageKeyup(event: KeyboardEvent): void {
    if (this.isMentionNavigationKey(event)) return
    this.onMessageInput(event)
  }

  private isMentionNavigationKey(event: KeyboardEvent): boolean {
    if (!this.showMentionPanel || !this.mentionResults.length) return false
    return event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === 'Tab' || event.key === 'Escape'
  }

  onMentionKeydown(event: KeyboardEvent): void {
    if (!this.isMentionNavigationKey(event)) return

    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault()
      const member = this.keyManager?.activeItem?.member
      if (member) this.selectMention(member)
    } else if (event.key === 'Escape') {
      this.closeMentionPanel()
    } else {
      this.keyManager?.onKeydown(event) // ArrowUp / ArrowDown
    }
  }

  /** A member's name in the picker and the @mention - the same as their name on a chat bubble
   * (the state code masked: the name goes into the message text). */
  mentionName(member: PublicCorpMember): string {
    return displayNameOf(member)
  }

  selectMention(member: PublicCorpMember): void {
    if (this.mentionTokenStart < 0) return

    const textarea = this.textareaRef?.nativeElement
    const value = this.internalControl.value ?? ''
    const caret = textarea?.selectionStart ?? value.length

    // Defensive: mentionTokenStart was recorded when the dropdown opened, but the caret can
    // move (a click elsewhere in this same textarea, or arrow-key navigation) without firing
    // an `input` or `blur` event to update/reset it. If the text between mentionTokenStart and
    // the *current* caret no longer looks like an uninterrupted "@query", that assumption no
    // longer holds — bail out instead of splicing into the wrong place.
    const trackedQueryText = value.slice(this.mentionTokenStart, caret)
    if (caret < this.mentionTokenStart || !/^@\w*$/.test(trackedQueryText)) {
      this.closeMentionPanel()
      return
    }

    // state_code is required on PublicCorpMember, so this always identifies someone — unlike a
    // generic 'Member' fallback, which is ambiguous when multiple mentioned users lack a name.
    const name = this.mentionName(member)
    const display = `@${name}`
    const insertText = display + ' '

    // Shift any already-tracked mentions that sit at/after the caret by however much the
    // replaced "@partialQuery" text grows/shrinks once swapped for the full display name.
    const delta = insertText.length - (caret - this.mentionTokenStart)
    this.activeMentions = this.activeMentions.map((m) =>
      m.start >= caret ? { ...m, start: m.start + delta, end: m.end + delta } : m
    )
    this.activeMentions.push({
      id: member.slug_id ?? String(member.id),
      name,
      start: this.mentionTokenStart,
      end: this.mentionTokenStart + display.length,
    })

    const newValue = value.slice(0, this.mentionTokenStart) + insertText + value.slice(caret)
    this.internalControl.setValue(newValue)
    this.lastValue = newValue // setValue() doesn't fire our (input) handler, so sync manually.

    const newCaretPos = this.mentionTokenStart + insertText.length
    this.closeMentionPanel()
    this.cdf.detectChanges()

    setTimeout(() => {
      textarea?.focus()
      textarea?.setSelectionRange(newCaretPos, newCaretPos)
    })
  }

  closeMentionPanel(): void {
    this.showMentionPanel = false
    this.mentionResults = []
    this.mentionTokenStart = -1
    this.activeQuery = null
  }

  /**
   * Renders the dropdown above the textarea by default, but flips it below when there isn't
   * enough room above in the viewport — otherwise it can render off-screen/clipped when the
   * chat panel (and its textarea) sits near the top of the window.
   */
  private computeMentionPanelPosition(textarea: HTMLTextAreaElement): 'above' | 'below' {
    const spaceAbove = textarea.getBoundingClientRect().top
    return spaceAbove >= this.mentionPanelMaxHeight ? 'above' : 'below'
  }

  /**
   * Keeps `activeMentions` ranges valid as the user types freely around them. Uses a simple
   * common-prefix/common-suffix diff against the last known value to locate the edited region:
   * mentions entirely before or after it just get shifted by the length delta; a mention whose
   * range overlaps the edited region is dropped (the user typed into it, so it degrades to plain
   * text — the same graceful-degradation behavior most mention implementations have).
   */
  private reconcileMentionsAfterEdit(newValue: string): void {
    const oldValue = this.lastValue
    this.lastValue = newValue

    if (oldValue === newValue || !this.activeMentions.length) return

    let prefixLen = 0
    const maxPrefix = Math.min(oldValue.length, newValue.length)
    while (prefixLen < maxPrefix && oldValue[prefixLen] === newValue[prefixLen]) prefixLen++

    let suffixLen = 0
    const maxSuffix = maxPrefix - prefixLen
    while (
      suffixLen < maxSuffix &&
      oldValue[oldValue.length - 1 - suffixLen] === newValue[newValue.length - 1 - suffixLen]
    ) {
      suffixLen++
    }

    const oldEditEnd = oldValue.length - suffixLen
    const delta = newValue.length - oldValue.length

    this.activeMentions = this.activeMentions
      .filter((m) => m.end <= prefixLen || m.start >= oldEditEnd)
      .map((m) => (m.start >= oldEditEnd ? { ...m, start: m.start + delta, end: m.end + delta } : m))
  }

  /**
   * Resolves an @mention query either from the local cache (instantly, no network) or by
   * falling through to the debounced network search. Checked in order:
   *  1. An exact cache hit for this query.
   *  2. The longest cached prefix of this query whose result set was exhaustive (not
   *     truncated) — since `name.includes(query)` implies `name.includes(prefix)` for any
   *     prefix of `query`, filtering that prefix's complete result set locally is guaranteed
   *     to produce the same result the server would.
   *  3. Otherwise, debounce and hit the network.
   */
  private searchMentionCandidates(rawQuery: string): void {
    const query = rawQuery.toLowerCase()
    this.activeQuery = query

    const exact = this.mentionSearchCache.get(query)
    if (exact) {
      this.applyMentionResults(exact.results)
      return
    }

    for (let len = query.length - 1; len >= 0; len--) {
      const prefix = query.slice(0, len)
      const cached = this.mentionSearchCache.get(prefix)
      if (cached && !cached.truncated) {
        const filtered = cached.results.filter(
          (member) =>
            (member.first_name ?? '').toLowerCase().includes(query) ||
            (member.nickname ?? '').toLowerCase().includes(query)
        )
        // A filter of an exhaustive set is itself exhaustive — safe to reuse for further narrowing.
        this.mentionSearchCache.set(query, { results: filtered, truncated: false })
        this.applyMentionResults(filtered)
        return
      }
    }

    // If a request for this exact query is already in flight (e.g. the focus-time prefetch,
    // for query ''), don't fire a duplicate — handleSearchResult() will pick it up once that
    // request resolves, since activeQuery now matches it.
    this.triggerMentionSearch(query)
  }

  private applyMentionResults(results: PublicCorpMember[]): void {
    this.mentionResults = results
    this.cdf.detectChanges() // renders the options, so the key manager has them
    this.keyManager?.setFirstItemActive()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }
}
